import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { AppButton, AppTextField } from '../design-system';
import { type Tenant } from '../../services/ProviderTenantsService';
import { providerEnterpriseService } from '../../services/ProviderEnterpriseService';
import { providerTenantsService } from '../../services/ProviderTenantsService';
import type {
  CreateEnterprisePackageRequest,
  EnterprisePackageData,
} from '@cnapse/shared/schemas.js';
import {
  ENTERPRISE_ENTITLEMENT_FEATURE_KEYS,
  getFeatureLabel,
  AXON_GATEWAY_LIMIT_LABEL,
} from '@cnapse/shared';
import * as dt from '@cnapse/shared/data.types.js';

const DEFAULT_LIMITS: NonNullable<CreateEnterprisePackageRequest['limits']> = {
  maxUsers: 10,
  maxContainers: 100,
  maxWafers: 10_000,
  maxAxonGateways: 2,
  maxStorageTb: 0.5,
};

type LimitKey = keyof typeof DEFAULT_LIMITS;

const EMPTY_FEATURE_FLAGS = Object.fromEntries(
  ENTERPRISE_ENTITLEMENT_FEATURE_KEYS.map((key) => [key, false]),
) as Record<string, boolean>;

const toDateInput = (value: Date | string | null | undefined): string => {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const fromDateInput = (value: string): Date | null =>
  value ? new Date(`${value}T23:59:59.999`) : null;

interface EnterprisePackageDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingPackage?: EnterprisePackageData | null;
}

const EnterprisePackageDialog: React.FC<EnterprisePackageDialogProps> = ({
  open,
  onClose,
  onSuccess,
  existingPackage,
}) => {
  const isEdit = !!existingPackage;
  const isReadOnly = !!existingPackage?.assignedSubscriptionId;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<dt.ENTERPRISE_PACKAGE_KIND>(dt.ENTERPRISE_PACKAGE_KIND.Pilot);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [trialStartsAt, setTrialStartsAt] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [contractPeriodStart, setContractPeriodStart] = useState('');
  const [contractPeriodEnd, setContractPeriodEnd] = useState('');
  const [invoiceDueAt, setInvoiceDueAt] = useState('');
  const [invoiceDueDays, setInvoiceDueDays] = useState('45');
  const [contractPoNumber, setContractPoNumber] = useState('');
  const [zohoCustomerId, setZohoCustomerId] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>(EMPTY_FEATURE_FLAGS);
  const [limitsPatchDraft, setLimitsPatchDraft] = useState<Partial<Record<LimitKey, string>>>({});

  const isAmendment = kind === dt.ENTERPRISE_PACKAGE_KIND.Amendment;

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadTenants();
    if (existingPackage) {
      setName(existingPackage.name);
      setDescription(existingPackage.description ?? '');
      setKind(existingPackage.kind as dt.ENTERPRISE_PACKAGE_KIND);
      setTrialStartsAt(toDateInput(existingPackage.trialStartsAt));
      setTrialEndsAt(toDateInput(existingPackage.trialEndsAt));
      setContractPeriodStart(toDateInput(existingPackage.contractPeriodStart));
      setContractPeriodEnd(toDateInput(existingPackage.contractPeriodEnd));
      setInvoiceDueAt(toDateInput(existingPackage.invoiceDueAt));
      setInvoiceDueDays(String(existingPackage.invoiceDueDays ?? 45));
      setContractPoNumber(existingPackage.contractPoNumber ?? '');
      setZohoCustomerId(existingPackage.zohoCustomerId ?? '');
      setInternalNotes(existingPackage.internalNotes ?? '');
      setLimits(existingPackage.limits);
      setFeatureFlags({
        ...EMPTY_FEATURE_FLAGS,
        ...Object.fromEntries(
          ENTERPRISE_ENTITLEMENT_FEATURE_KEYS.map((key) => [
            key,
            (existingPackage.features ?? {})[key] === true,
          ]),
        ),
      });
      setLimitsPatchDraft(
        Object.fromEntries(
          Object.entries(existingPackage.limitsPatch ?? {}).map(([key, value]) => [key, String(value)]),
        ) as Partial<Record<LimitKey, string>>,
      );
    } else {
      setName('');
      setDescription('');
      setKind(dt.ENTERPRISE_PACKAGE_KIND.Pilot);
      setSelectedTenant(null);
      setTrialStartsAt(toDateInput(new Date()));
      const end = new Date();
      end.setDate(end.getDate() + 90);
      setTrialEndsAt(toDateInput(end));
      setContractPeriodStart('');
      setContractPeriodEnd('');
      setInvoiceDueAt('');
      setInvoiceDueDays('45');
      setContractPoNumber('');
      setZohoCustomerId('');
      setInternalNotes('');
      setLimits(DEFAULT_LIMITS);
      setFeatureFlags(EMPTY_FEATURE_FLAGS);
      setLimitsPatchDraft({});
    }
  }, [open, existingPackage]);

  useEffect(() => {
    if (!open || !existingPackage || tenants.length === 0) {
      return;
    }
    if (existingPackage.tenantId) {
      setSelectedTenant(tenants.find((t) => t.id === existingPackage.tenantId) ?? null);
    }
  }, [open, existingPackage, tenants]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const tenantList = await providerTenantsService.getTenants();
      setTenants(tenantList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (field: LimitKey, value: string) => {
    setLimits((prev) => ({
      ...prev,
      [field]: field === 'maxStorageTb' ? Number(value) : Number.parseInt(value, 10),
    }));
  };

  const buildLimitsPatch = (): CreateEnterprisePackageRequest['limitsPatch'] => {
    const patch: Partial<Record<LimitKey, number>> = {};
    for (const key of Object.keys(limitsPatchDraft) as LimitKey[]) {
      const raw = limitsPatchDraft[key];
      if (raw === undefined || raw === '') {
        continue;
      }
      patch[key] = key === 'maxStorageTb' ? Number(raw) : Number.parseInt(raw, 10);
    }
    return Object.keys(patch).length > 0 ? patch : undefined;
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Package name is required');
      return;
    }
    if (isAmendment && !selectedTenant) {
      setError('Amendment packages require a linked tenant');
      return;
    }

    const payload: CreateEnterprisePackageRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      kind,
      tenantId: selectedTenant?.id ?? null,
      contractPoNumber: contractPoNumber || null,
      zohoCustomerId: zohoCustomerId || null,
      internalNotes: internalNotes || null,
    };

    if (isAmendment) {
      payload.features = featureFlags;
      payload.limitsPatch = buildLimitsPatch();
    } else {
      payload.limits = limits;
      const enabledFeatures = Object.fromEntries(
        Object.entries(featureFlags).filter(([, enabled]) => enabled),
      );
      if (Object.keys(enabledFeatures).length > 0) {
        payload.features = enabledFeatures;
      }
    }

    if (kind === dt.ENTERPRISE_PACKAGE_KIND.Pilot) {
      payload.trialStartsAt = fromDateInput(trialStartsAt);
      payload.trialEndsAt = fromDateInput(trialEndsAt);
    } else if (kind === dt.ENTERPRISE_PACKAGE_KIND.Annual) {
      payload.contractPeriodStart = fromDateInput(contractPeriodStart);
      payload.contractPeriodEnd = fromDateInput(contractPeriodEnd);
      payload.invoiceDueAt = fromDateInput(invoiceDueAt);
      payload.invoiceDueDays = invoiceDueDays ? Number(invoiceDueDays) : null;
    }

    try {
      setSubmitting(true);
      setError(null);
      if (isEdit && existingPackage) {
        await providerEnterpriseService.updateEnterprisePackage(existingPackage.packageId, payload);
      } else {
        await providerEnterpriseService.createEnterprisePackage(payload);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save package');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isReadOnly
          ? 'View enterprise package'
          : isEdit
            ? 'Edit enterprise package'
            : 'Create enterprise package'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack
            component="fieldset"
            disabled={isReadOnly}
            spacing={2}
            sx={{ mt: 1, border: 'none', m: 0, p: 0, minWidth: 0 }}
          >
            {error && <Alert severity="error">{error}</Alert>}
            {isReadOnly && (
              <Alert severity="info">
                This package is assigned to a tenant subscription and cannot be edited.
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              {isAmendment
                ? 'Define entitlement changes for an existing enterprise subscription. Set features on or off and optionally patch limits. Pricing stays in Zoho.'
                : 'Define the full deal upfront. Pricing and invoicing stay in Zoho — use internal notes only for commercial context.'}
            </Typography>

            <AppTextField label="Package name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
            <AppTextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth disabled={isEdit}>
              <InputLabel>Kind</InputLabel>
              <Select
                label="Kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as dt.ENTERPRISE_PACKAGE_KIND)}
              >
                <MenuItem value={dt.ENTERPRISE_PACKAGE_KIND.Pilot}>Pilot</MenuItem>
                <MenuItem value={dt.ENTERPRISE_PACKAGE_KIND.Annual}>Annual contract</MenuItem>
                <MenuItem value={dt.ENTERPRISE_PACKAGE_KIND.Amendment}>Amendment (add-on / change)</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              options={tenants}
              getOptionLabel={(t) => t.name}
              value={selectedTenant}
              onChange={(_, value) => setSelectedTenant(value)}
              renderInput={(params) => (
                <AppTextField
                  {...params}
                  label={isAmendment ? 'Linked tenant' : 'Linked tenant (optional)'}
                  required={isAmendment}
                  helperText={
                    isAmendment
                      ? 'Required — amendment applies to this customer’s active subscription'
                      : 'Restrict assignment to this customer'
                  }
                />
              )}
            />

            {kind === dt.ENTERPRISE_PACKAGE_KIND.Pilot && (
              <>
                <AppTextField
                  label="Trial starts"
                  type="date"
                  value={trialStartsAt}
                  onChange={(e) => setTrialStartsAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <AppTextField
                  label="Trial ends"
                  type="date"
                  value={trialEndsAt}
                  onChange={(e) => setTrialEndsAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </>
            )}

            {kind === dt.ENTERPRISE_PACKAGE_KIND.Annual && (
              <>
                <AppTextField
                  label="Contract period start"
                  type="date"
                  value={contractPeriodStart}
                  onChange={(e) => setContractPeriodStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
                <AppTextField
                  label="Contract period end"
                  type="date"
                  value={contractPeriodEnd}
                  onChange={(e) => setContractPeriodEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
                <AppTextField
                  label="Invoice due date (optional)"
                  type="date"
                  value={invoiceDueAt}
                  onChange={(e) => setInvoiceDueAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <AppTextField
                  label="Invoice due (Net days)"
                  type="number"
                  value={invoiceDueDays}
                  onChange={(e) => setInvoiceDueDays(e.target.value)}
                  fullWidth
                />
              </>
            )}

            <AppTextField
              label="PO number"
              value={contractPoNumber}
              onChange={(e) => setContractPoNumber(e.target.value)}
              fullWidth
            />
            <AppTextField
              label="Zoho Books customer ID (ops)"
              value={zohoCustomerId}
              onChange={(e) => setZohoCustomerId(e.target.value)}
              fullWidth
            />

            <Divider />
            <Typography variant="subtitle2">Module entitlements</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {isAmendment
                ? 'Toggle add-on modules on or off. Disabled modules are explicitly removed from the subscription. CCI Operations is always included.'
                : 'CCI Operations is included in every package. Enable optional add-on modules below.'}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0.5,
                columnGap: 2,
              }}
            >
              {ENTERPRISE_ENTITLEMENT_FEATURE_KEYS.map((featureKey) => (
                <FormControlLabel
                  key={featureKey}
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={featureFlags[featureKey] ?? false}
                      onChange={(e) =>
                        setFeatureFlags((prev) => ({ ...prev, [featureKey]: e.target.checked }))
                      }
                    />
                  }
                  label={getFeatureLabel(featureKey)}
                />
              ))}
            </Box>

            {!isAmendment && (
              <>
            <Divider />
            <Typography variant="subtitle2">Entitlement limits</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <AppTextField
                label="Max users"
                type="number"
                value={limits.maxUsers}
                onChange={(e) => handleLimitChange('maxUsers', e.target.value)}
                fullWidth
              />
              <AppTextField
                label="Max containers"
                type="number"
                value={limits.maxContainers}
                onChange={(e) => handleLimitChange('maxContainers', e.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <AppTextField
                label="Max wafers"
                type="number"
                value={limits.maxWafers}
                onChange={(e) => handleLimitChange('maxWafers', e.target.value)}
                fullWidth
              />
              <AppTextField
                label={AXON_GATEWAY_LIMIT_LABEL}
                type="number"
                value={limits.maxAxonGateways}
                onChange={(e) => handleLimitChange('maxAxonGateways', e.target.value)}
                fullWidth
              />
            </Stack>
            <AppTextField
              label="Max storage (TB)"
              type="number"
              value={limits.maxStorageTb}
              onChange={(e) => handleLimitChange('maxStorageTb', e.target.value)}
              fullWidth
            />
              </>
            )}

            {isAmendment && (
              <>
                <Divider />
                <Typography variant="subtitle2">Limit changes (optional)</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Only fill in fields you are changing. Leave blank to keep current subscription limits.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <AppTextField
                    label="Max users"
                    type="number"
                    value={limitsPatchDraft.maxUsers ?? ''}
                    onChange={(e) =>
                      setLimitsPatchDraft((prev) => ({ ...prev, maxUsers: e.target.value }))
                    }
                    fullWidth
                  />
                  <AppTextField
                    label="Max containers"
                    type="number"
                    value={limitsPatchDraft.maxContainers ?? ''}
                    onChange={(e) =>
                      setLimitsPatchDraft((prev) => ({ ...prev, maxContainers: e.target.value }))
                    }
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <AppTextField
                    label="Max wafers"
                    type="number"
                    value={limitsPatchDraft.maxWafers ?? ''}
                    onChange={(e) =>
                      setLimitsPatchDraft((prev) => ({ ...prev, maxWafers: e.target.value }))
                    }
                    fullWidth
                  />
                  <AppTextField
                    label={AXON_GATEWAY_LIMIT_LABEL}
                    type="number"
                    value={limitsPatchDraft.maxAxonGateways ?? ''}
                    onChange={(e) =>
                      setLimitsPatchDraft((prev) => ({ ...prev, maxAxonGateways: e.target.value }))
                    }
                    fullWidth
                  />
                </Stack>
                <AppTextField
                  label="Max storage (TB)"
                  type="number"
                  value={limitsPatchDraft.maxStorageTb ?? ''}
                  onChange={(e) =>
                    setLimitsPatchDraft((prev) => ({ ...prev, maxStorageTb: e.target.value }))
                  }
                  fullWidth
                />
              </>
            )}

            <AppTextField
              label="Internal notes"
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              helperText="Commercial context only — not shown to customers"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <AppButton onClick={onClose} disabled={submitting}>
          {isReadOnly ? 'Close' : 'Cancel'}
        </AppButton>
        {!isReadOnly && (
          <AppButton variant="contained" onClick={() => void handleSubmit()} disabled={submitting || loading}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create package'}
          </AppButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnterprisePackageDialog;
